import React, { useState, useRef } from 'react';
import axios from 'axios';
import { server } from "../../server";
import { useSelector } from "react-redux";
import CustomCalendar from './CustomCalendar';
import ImageCropperModal from '../Image/ImageCropperModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { GOOGLE_MAPS_LIBRARIES } from '../../constants/googleMaps';
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from '../../utils/imageUploadPreparation';

const parseEventDateTime = (dateValue, timeValue, fallbackToEndOfDay = false) => {
  const eventDate = new Date(dateValue);

  if (Number.isNaN(eventDate.getTime())) {
    return null;
  }

  if (typeof timeValue === 'string' && /^\d{2}:\d{2}$/.test(timeValue)) {
    const [hours, minutes] = timeValue.split(':').map(Number);
    eventDate.setHours(hours, minutes, 0, 0);
  } else if (fallbackToEndOfDay) {
    eventDate.setHours(23, 59, 59, 999);
  } else {
    eventDate.setHours(0, 0, 0, 0);
  }

  return eventDate;
};

const getEventErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData?.message === 'string' && responseData.message.trim()) {
    return responseData.message;
  }

  if (Array.isArray(responseData?.errors)) {
    const firstError = responseData.errors.find(
      (entry) => typeof entry?.message === 'string' && entry.message.trim()
    );

    if (firstError) {
      return firstError.message;
    }
  }

  return 'Failed to create event. Please try again.';
};

const getEventProfileName = (currentProfile) => {
  if (currentProfile && currentProfile !== 'Guest' && currentProfile !== 'defaultProfile') {
    return currentProfile;
  }

  if (typeof sessionStorage !== 'undefined') {
    const storedProfile = sessionStorage.getItem('currentProfile');
    if (storedProfile && storedProfile !== 'Guest' && storedProfile !== 'defaultProfile') {
      return storedProfile;
    }
  }

  return 'User';
};

const EventCreationForm = ({ onClose, onCreated }) => {
  const currentProfile = useSelector((state) => state.user.currentProfile);

  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: null,
    date: new Date(),
    endDate: new Date(),
    location: '',
    startTime: '',
    endTime: '',
  });

  // State for Google Autocomplete
  const [autocomplete, setAutocomplete] = useState(null);

  // State variables for Image Cropper Modal
  const [selectedFile, setSelectedFile] = useState(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Loading and error handling
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPreparingImage, setIsPreparingImage] = useState(false);

  /** Handle text/input changes */
  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    if (name === 'image') {
      const file = files?.[0];
      e.target.value = '';

      if (file) {
        setError(null);
        setFormData((prev) => ({ ...prev, image: null }));
        setPreviewUrl(null);
        setIsPreparingImage(true);

        try {
          const preparedFile = await prepareImageFileForUpload(file, {
            optimizeHeic: false,
            timeoutMs: 60000,
          });
          setSelectedFile(preparedFile);
          setShowImageCropper(true);
        } catch (error) {
          console.error('Error preparing event image:', error);
          const message = getImagePreparationErrorMessage(error);
          setSelectedFile(null);
          setShowImageCropper(false);
          setError(message);
          toast.error(message);
        } finally {
          setIsPreparingImage(false);
        }
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /** Update the start date */
  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, date }));
  };

  /** Update the end date */
  const handleEndDateChange = (endDate) => {
    setFormData((prev) => ({ ...prev, endDate }));
  };

  /** Callback to receive cropped image */
  const updateCroppedImage = (croppedFile) => {
    setFormData((prev) => ({ ...prev, image: croppedFile }));
    setPreviewUrl(URL.createObjectURL(croppedFile));
    setShowImageCropper(false);
  };

  /** Google Autocomplete handlers **/
  const handleLoad = (autocompleteInstance) => {
    setAutocomplete(autocompleteInstance);
  };

  const handlePlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (!place || !place.address_components) {
        toast.error("No address details available.");
        return;
      }
      if (place.formatted_address) {
        setFormData((prev) => ({
          ...prev,
          location: place.formatted_address,
        }));
      }

    } else {
      toast.error("Autocomplete is not loaded yet!");
    }
  };

  /** Example parse function if you need more granular data (city, state, etc.) */
  const parseAddressComponents = (components) => {
    let streetAddress = "";
    let city = "";
    let state = "";
    let zipCode = "";
    let country = "";

    components.forEach((component) => {
      const types = component.types;
      if (types.includes("street_number")) {
        streetAddress = component.long_name + " ";
      }
      if (types.includes("route")) {
        streetAddress += component.long_name;
      }
      if (types.includes("locality")) {
        city = component.long_name;
      }
      if (types.includes("administrative_area_level_1")) {
        state = component.short_name;
      }
      if (types.includes("postal_code")) {
        zipCode = component.long_name;
      }
      if (types.includes("country")) {
        country = component.short_name;
      }
    });

    return { streetAddress, city, state, zipCode, country };
  };

  /** Form submission handler */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const normalizedTitle = formData.title.trim();
    const normalizedDescription = formData.description.trim();
    const normalizedLocation = formData.location.trim();

    // Basic front-end validation
    if (
      !normalizedTitle ||
      !normalizedDescription ||
      !formData.image ||
      !normalizedLocation ||
      !formData.startTime
    ) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const startAt = parseEventDateTime(formData.date, formData.startTime);
    const endAt = parseEventDateTime(
      formData.endDate || formData.date,
      formData.endTime,
      true
    );

    if (startAt && endAt && endAt < startAt) {
      setError('Event end date/time cannot be before the start date/time.');
      setLoading(false);
      return;
    }

    const data = new FormData();
    const activeProfile = getEventProfileName(currentProfile);
    data.append('title', normalizedTitle);
    data.append('description', normalizedDescription);
    data.append('image', formData.image);
    data.append('date', formData.date.toISOString());
    data.append('endDate', formData.endDate.toISOString());
    data.append('location', normalizedLocation);
    data.append('startTime', formData.startTime);
    data.append('endTime', formData.endTime);
    data.append('currentProfile', activeProfile);

    try {
      await axios.post(`${server}/event/create`, data, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          'current-profile': activeProfile,
        },
      });

      toast.success('Event created successfully!');
      // Reset form
      setFormData({
        title: '',
        description: '',
        image: null,
        date: new Date(),
        endDate: new Date(),
        location: '',
        startTime: '',
        endTime: '',
      });
      setPreviewUrl(null);
      setSelectedFile(null);
      onCreated?.();
      onClose?.();
    } catch (error) {
      const message = getEventErrorMessage(error);
      console.error('Error creating event:', error);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="event-creation-form-container">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />

      <form onSubmit={handleSubmit} className="event-creation-form-form">
        <div className="form-group">
          <label htmlFor="title" className="event-creation-form-label">
            Event Title <span className="required-marker">*</span>
          </label>
          <input
            type="text"
            name="title"
            id="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Event Title"
            required
            className="event-creation-form-input"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description" className="event-creation-form-label">
            Event Description <span className="required-marker">*</span>
          </label>
          <textarea
            name="description"
            id="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Event Description"
            required
            className="event-creation-form-textarea"
          />
        </div>

        <div className="form-group">
          <label htmlFor="image" className="event-creation-form-label">
            Event Image <span className="required-marker">*</span>
          </label>
          <label htmlFor="image-upload" className="event-upload-label">
            <FontAwesomeIcon icon={faUpload} /> {isPreparingImage ? 'Preparing Image...' : 'Upload Image'}
          </label>
          <input
            id="image-upload"
            type="file"
            name="image"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleChange}
            disabled={isPreparingImage || loading}
            style={{ display: 'none' }}
          />
        </div>

        {/* Optional: Display Image Preview */}
        {previewUrl && (
          <div className="image-preview">
            <img src={previewUrl} alt="Cropped Preview" className="preview-image" />
          </div>
        )}

        <div className="form-group">
          <label htmlFor="location" className="event-creation-form-label">
            Event Location <span className="required-marker">*</span>
          </label>

          {mapsLoaded ? (
            <Autocomplete onLoad={handleLoad} onPlaceChanged={handlePlaceChanged}>
              <input
                type="text"
                name="location"
                id="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Type or select an address..."
                required
                className="event-creation-form-input"
              />
            </Autocomplete>
          ) : (
            <input
              type="text"
              name="location"
              id="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Type or select an address..."
              required
              className="event-creation-form-input"
            />
          )}
        </div>

        <div className="event-creation-form-date-time-row">
          <div className="date-time-group">
            <label htmlFor="date" className="event-creation-form-label">
              Event Start Date <span className="required-marker">*</span>
            </label>
            <CustomCalendar 
              selectedDate={formData.date} 
              onDateChange={handleDateChange} 
            />

            <label htmlFor="startTime" className="event-creation-form-label">
              Event Start Time <span className="required-marker">*</span>
            </label>
            <input
              type="time"
              name="startTime"
              id="startTime"
              value={formData.startTime}
              onChange={handleChange}
              required
              className="event-creation-form-input time-input"
            />
          </div>

          <div className="date-time-group">
            <label htmlFor="endDate" className="event-creation-form-label">
              Event End Date
            </label>
            <CustomCalendar
              selectedDate={formData.endDate}
              onDateChange={handleEndDateChange}
            />

            <label htmlFor="endTime" className="event-creation-form-label">
              Event End Time
            </label>
            <input
              type="time"
              name="endTime"
              id="endTime"
              value={formData.endTime}
              onChange={handleChange}
              className="event-creation-form-input time-input"
            />
          </div>
        </div>

        {error && <div className="event-error-message">{error}</div>}

        <button
          type="submit"
          className="event-creation-form-button"
          disabled={loading || isPreparingImage || showImageCropper}
        >
          {loading ? 'Creating Event...' : isPreparingImage ? 'Preparing Image...' : showImageCropper ? 'Finish Image Crop' : 'Create Event'}
        </button>
      </form>

      {/* Image Cropper Modal */}
      {showImageCropper && (
        <ImageCropperModal
          updateAvatar={updateCroppedImage}
          closeModal={() => setShowImageCropper(false)}
          initialImage={selectedFile}
          aspectRatio={1}
          minWidth={320}
          isAvatar={false}
          isPost={false}
          isLogo={false}
        />
      )}
    </div>
  );
};

export default EventCreationForm;





